import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useApiQuery } from './useApiQuery';

interface ProfilePhotoResponse {
  photo: string | null;
}

/**
 * The current user's avatar URL.
 *
 * Prefers the LIVE Google/Microsoft photo from a connected calendar (the most
 * current corporate avatar, fetched via the BFF `/calendar/me/photo` proxy),
 * falling back to the LinkedIn photo persisted on the user object, then to
 * `undefined` so an AntD `<Avatar>` renders its initial child.
 *
 * The lookup is cached (30 min) and never retries — a missing/absent photo or a
 * provider hiccup simply yields `undefined`, so the avatar always degrades to
 * the initial instead of erroring.
 */
export function useProfilePhoto(): string | undefined {
  const { user } = useAuth();
  const { data } = useApiQuery<ProfilePhotoResponse>(
    ['profile-photo'],
    async () => {
      const res = await api.get<ProfilePhotoResponse>('/calendar/me/photo');
      return res.data;
    },
    {
      enabled: Boolean(user),
      staleTime: 30 * 60 * 1000,
      retry: false,
    },
  );
  return data?.photo ?? user?.profilePhotoUrl ?? undefined;
}
